import React, { useEffect, useState } from "react";

import { Dialog, Input, TextArea } from "@fronterahealth/frontera-ui-components";

interface DomainEditDialogProps {
  open: boolean;
  /** Empty when adding a brand-new domain. */
  initialName: string;
  initialDescription: string;
  title: string;
  primaryLabel: string;
  onClose: () => void;
  onSubmit: (next: { name: string; description: string }) => void;
}

/** Sub-dialog used by ManageDomainsDialog for renaming/describing a single
 *  domain (or creating a new one). Buffered locally — the parent's pending
 *  state isn't updated until Save is clicked. */
export const DomainEditDialog: React.FC<DomainEditDialogProps> = ({
  open,
  initialName,
  initialDescription,
  title,
  primaryLabel,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setDescription(initialDescription);
      setTouched(false);
    }
  }, [open, initialName, initialDescription]);

  const trimmedName = name.trim();
  const nameInvalid = touched && trimmedName.length === 0;

  const handleSubmit = () => {
    setTouched(true);
    if (trimmedName.length === 0) return;
    onSubmit({ name: trimmedName, description: description.trim() });
  };

  return (
    <Dialog
      open={open}
      setOpen={() => {}}
      title={title}
      size="lg"
      showCloseButton={false}
      primaryButton={{ text: primaryLabel, onClick: handleSubmit }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
    >
      <div className="space-y-4 py-2">
        <div>
          <Input
            name="domain-edit-name"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Domain name"
          />
          {nameInvalid && <p className="mt-1 text-xs text-red-600">Name is required.</p>}
        </div>
        <TextArea
          name="domain-edit-description"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description"
          rows={3}
        />
      </div>
    </Dialog>
  );
};
